import React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { AlertTriangle, CheckCircle, XCircle, Info, X } from 'lucide-react';

export interface ConfirmOptions {
  title?: string;
  message: string;
  type?: 'warning' | 'danger' | 'success' | 'info';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCancel?: boolean;
}

interface CustomUIProps {
  title?: string;
  message: string;
  type: 'warning' | 'danger' | 'success' | 'info';
  confirmText: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  showCancel: boolean;
  onClickOutside: () => void;
}

const CustomUI: React.FC<CustomUIProps> = ({
  title,
  message,
  type,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  showCancel,
  onClickOutside,
}) => {
  const getIcon = () => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-yellow-600" />;
      case 'danger':
        return <XCircle className="w-6 h-6 text-red-600" />;
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'info':
        return <Info className="w-6 h-6 text-blue-600" />;
      default:
        return <Info className="w-6 h-6 text-blue-600" />;
    }
  };

  const getConfirmButtonClass = () => {
    switch (type) {
      case 'warning':
        return 'btn-warning';
      case 'danger':
        return 'btn-danger';
      case 'success':
        return 'btn-success';
      case 'info':
        return 'btn-primary';
      default:
        return 'btn-primary';
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClickOutside();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 animate-fade-in"
        onClick={onClickOutside}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 animate-fade-in">
        {/* Close button */}
        <button
          onClick={onClickOutside}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Icon and Title */}
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 mr-3">
              {getIcon()}
            </div>
            <div className="flex-1">
              {title && (
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  {title}
                </h3>
              )}
            </div>
          </div>

          {/* Message */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 leading-relaxed">
              {message}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            {showCancel && (
              <button
                type="button"
                className="btn-outline"
                onClick={handleCancel}
              >
                {cancelText || 'Cancelar'}
              </button>
            )}
            <button
              type="button"
              className={`btn ${getConfirmButtonClass()}`}
              onClick={handleConfirm}
              autoFocus
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const showConfirm = (options: ConfirmOptions) => {
  const {
    title,
    message,
    type = 'info',
    confirmText = 'Aceptar',
    cancelText = 'Cancelar',
    onConfirm,
    onCancel,
    showCancel = true,
  } = options;

  confirmAlert({
    customUI: ({ onClose }) => (
      <CustomUI
        title={title}
        message={message}
        type={type}
        confirmText={confirmText}
        cancelText={cancelText}
        onConfirm={() => {
          if (onConfirm) onConfirm();
          onClose();
        }}
        onCancel={() => {
          if (onCancel) onCancel();
          onClose();
        }}
        showCancel={showCancel}
        onClickOutside={onClose}
      />
    ),
  });
};

// Convenience functions for different alert types
export const showWarning = (message: string, options?: Partial<ConfirmOptions>) => {
  showConfirm({
    type: 'warning',
    title: 'Advertencia',
    message,
    showCancel: false,
    confirmText: 'Entendido',
    ...options,
  });
};

export const showError = (message: string, options?: Partial<ConfirmOptions>) => {
  showConfirm({
    type: 'danger',
    title: 'Error',
    message,
    showCancel: false,
    confirmText: 'Aceptar',
    ...options,
  });
};

export const showSuccess = (message: string, options?: Partial<ConfirmOptions>) => {
  showConfirm({
    type: 'success',
    title: 'Éxito',
    message,
    showCancel: false,
    confirmText: 'Aceptar',
    ...options,
  });
};

export const showInfo = (message: string, options?: Partial<ConfirmOptions>) => {
  showConfirm({
    type: 'info',
    title: 'Información',
    message,
    showCancel: false,
    confirmText: 'Aceptar',
    ...options,
  });
};

export const showDeleteConfirm = (
  message: string = '¿Estás seguro de que deseas eliminar este elemento?',
  onConfirm?: () => void,
  confirmText?: string,
  type?: 'warning' | 'danger' | 'success' | 'info'
) => {
  showConfirm({
    type: type || 'danger',
    title: 'Confirmar acción',
    message,
    confirmText: confirmText || 'Eliminar',
    cancelText: 'Cancelar',
    showCancel: true,
    onConfirm,
  });
};